// src/modules/app-views/companies/pages/Document/tabs/EnrichmentTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Flex, Empty, Spin, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import translateX from 'utils/translateX';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import EnrichedEntityCard from 'components/JarvislyComponents/EnrichedEntityCard';
import showEnrichedEntityModal from 'components/JarvislyComponents/EnrichedEntityModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EnrichmentTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const entityId = data?._id;
  const cpfValue = data?.cpf?.value || '';
  const cnpjValue = data?.cnpj?.value || '';
  const documentValue = cnpjValue || cpfValue;
  const documentType = cnpjValue ? 'cnpj' : cpfValue ? 'cpf' : null;
  const isCompany = data?.entityType === 'company';

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (entityId) {
      fetchEnrichmentData();
    }
  }, [entityId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  async function fetchEnrichmentData() {
    setLoading(true);
    try {
      const url = `/v1/data-search/by-entity/${entityId}`;
      const [result] = await axiosService.get({ url, returnAsObject: true });
      if (result?.doc) {
        setEnrichmentData(result.doc);
      }
    } catch (error) {
      console.error('[EnrichmentTab] Error fetching enrichment data:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleRefresh() {
    // Refetch enrichment data after successful refresh
    fetchEnrichmentData();
  }

  function handleSearchClick() {
    if (!documentType || !documentValue) {
      appService.modal({
        flag: 'info',
        title: 'enrichment_requires_document',
        content: 'enrichment_requires_document_description',
      });
      return;
    }

    showEnrichedEntityModal({
      entityId,
      documentValue,
      documentType,
      onSuccess: () => fetchEnrichmentData(),
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const hasEnrichmentData =
    enrichmentData &&
    (enrichmentData.cnpjData ||
      enrichmentData.enrichment?.cep ||
      enrichmentData.enrichment?.geolocation ||
      enrichmentData.enrichment?.whatsapp);

  // cnpjData from dataSearch is ALREADY normalized - use directly
  const entity = enrichmentData?.cnpjData;

  // Get enrichment data
  const enrichment = enrichmentData?.enrichment || {};

  // Meta info from dataSearch
  const meta = {
    engine: enrichmentData?.engine,
    creditCost: enrichmentData?.creditCost,
  };

  // Data creation date for freshness alert
  // The createdAt field is in _metadata.audit.createdAt for dataSearch documents
  const dataCreatedAt = enrichmentData?._metadata?.audit?.createdAt || enrichmentData?.createdAt;

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ padding: 48 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (!hasEnrichmentData) {
    return (
      <Flex vertical align="center" gap={16} style={{ padding: 48 }}>
        <Empty description={translateX('no_enrichment_data')} />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearchClick}
        >
          {translateX(isCompany ? 'search_company' : 'search_person')}
        </Button>
      </Flex>
    );
  }

  return (
    <div>
      <EnrichedEntityCard
        entity={entity}
        enrichment={enrichment}
        meta={meta}
        showHeader={false}
        showNameInBasicData={true}
        showFooter={true}
        showActions={false}
        dataCreatedAt={dataCreatedAt}
        entityId={entityId}
        onRefresh={handleRefresh}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EnrichmentTab;
